import { useCallback, useState } from "react"
import { Sparkle } from "lucide-react"
import { useLocalStorage } from "usehooks-ts"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { FleetTodayContent } from "@/components/chatList/FleetTodayContent"
import {
  useAskSlickAI,
  useFleetQuestionContext,
  useFleetTodayGate,
} from "@/components/chatList/useFleetToday"
import { usePrivateAgentTarget } from "@/hooks/usePrivateAgentTarget"
import { TAB_TYPES, type TabType } from "@/reducers/tabsSlice"

type SlickAIButtonProps = {
  onActiveChatChange: (chatJid: string, tab?: TabType) => void
}

type SlickAIPopoverProps = {
  agentJid: string
  hasUnread: boolean
  onOpenAgent: () => Promise<void>
}

const BUTTON_LABEL = "Slick AI"
const SEEN_STORAGE_KEY = "slickAIButtonSeen"

export function SlickAIButton(props: SlickAIButtonProps) {
  if (!useFleetTodayGate()) return null
  return <SlickAIButtonWithTarget {...props} />
}

function SlickAIButtonWithTarget({ onActiveChatChange }: SlickAIButtonProps) {
  const openSlickAITab = useCallback(
    (chatJid: string) => onActiveChatChange(chatJid, TAB_TYPES.SLICK_AI),
    [onActiveChatChange],
  )
  const { openPrivateAgentChat, targetJid, unreadCount } =
    usePrivateAgentTarget(openSlickAITab)
  const openAgent = useCallback(
    () => openPrivateAgentChat("ChatListHeaderSlickAIButton"),
    [openPrivateAgentChat],
  )

  if (!targetJid) return null
  return (
    <SlickAIPopover
      agentJid={targetJid}
      hasUnread={unreadCount > 0}
      onOpenAgent={openAgent}
    />
  )
}

function SlickAIPopover({
  agentJid,
  hasUnread,
  onOpenAgent,
}: SlickAIPopoverProps) {
  const questionContext = useFleetQuestionContext()
  const ask = useAskSlickAI(agentJid, onOpenAgent)
  const [open, setOpen] = useState(false)
  const [seen, setSeen] = useLocalStorage(SEEN_STORAGE_KEY, false)

  const run = (action: string, send: () => unknown) => {
    window.analytics.track("FleetTodayClick", { action })
    void send()
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setSeen(true)
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            hasUnread ? `${BUTTON_LABEL} (unread messages)` : BUTTON_LABEL
          }
          title={BUTTON_LABEL}
          data-seen={seen}
          className="relative text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:bg-gray-800"
        >
          <Sparkle className="h-5 w-5" />
          {/* Pulses until the user first opens the button. */}
          {!seen && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0.5 rounded-md ring-2 ring-violet-400 motion-safe:animate-pulse"
            />
          )}
          {hasUnread && (
            <span
              aria-hidden
              className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-violet-600"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        collisionPadding={8}
        aria-label={BUTTON_LABEL}
        className="max-h-[min(80vh,var(--radix-popover-content-available-height))] w-[380px] overflow-y-auto p-3"
      >
        <FleetTodayContent
          questionContext={questionContext}
          ask={(action, question) => run(action, () => ask(question))}
          onOpenAgent={() => run("own", onOpenAgent)}
        />
      </PopoverContent>
    </Popover>
  )
}
